import { Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { distinctUntilChanged, skipWhile, take } from 'rxjs/operators';

import { SpinnerService } from 'src/app/shared/spinner/spinner.service';

import { DocumentService } from '../../../../core/api/document.service';
import { ReportService } from '../../../../core/api/report.service';
import { SiteService } from '../../../../core/api/site.service';
import { FormatHelperService } from '../../../../core/helper/format-helper.service';
import { SiteHelperService } from '../../../../core/helper/site.service';
import { ReportType } from '../../../../shared/constants/report_types.constants';
import { Buyer, Program, Site, Stand, User } from '../../../../shared/models';
import { ReportConfig } from '../../../../shared/models/report-config.model';
import { ReportResponse } from '../../../../shared/models/report-response.model';
import * as fromApp from '../../../../store';
import { selectAppConfigState } from '../../../../store/app-config/app-config.selectors';
import { selectCurrentUser } from '../../../../store/auth/auth.selectors';
import { selectSelectedStands } from '../../../../store/parcel/parcel.selectors';
import {
  updateSiteFailure,
  updateSiteStep,
  updateSubmitEligibilitySiteSuccessAndNavigate,
} from '../../../../store/site/site.actions';
import { selectActiveSite } from '../../../../store/site/site.selectors';

@Component({
  selector: 'fc-eligibility-questions',
  templateUrl: './eligibility-questions.component.html',
  styleUrls: ['./eligibility-questions.component.scss'],
})
export class EligibilityQuestionsComponent implements OnInit {
  @ViewChild('documentUpload', { static: false })
  documentUpload: ElementRef;
  destroyRef = inject(DestroyRef);

  eligibilityForm: UntypedFormGroup;
  activeSite: Site = null;
  docFiles: any[] = [];

  siteId: number;
  forestDataUpdatedDate: string;
  selectedStandList: Stand[] = [];
  allowedClearing = '0.0';
  showFirewoodQuestion = false;
  currentUser: User;
  preferredBuyer: Buyer;
  selectedPrograms: Program[] = [];
  reportConfig: ReportConfig = null;
  redProgramFlag: boolean = false;

  constructor(
    readonly documentService: DocumentService,
    readonly store: Store<fromApp.AppState>,
    readonly route: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    public siteHelper: SiteHelperService,
    public formatHelper: FormatHelperService,
    readonly siteService: SiteService,
    readonly spinnerService: SpinnerService,
    readonly reportService: ReportService,
    private router: Router
  ) {
    this.siteId = +this.route.snapshot.params.id;

    this.store.dispatch(
      updateSiteStep({
        id: this.siteId,
        step: 'Eligibility Check',
      })
    );

    this.eligibilityForm = this.formBuilder.group({});

    this.store
      .select(selectSelectedStands)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(stands => (this.selectedStandList = stands));
    this.store
      .select(selectAppConfigState)
      .pipe(
        skipWhile(appConfig => appConfig.config.length === 0),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(appConfig => {
        this.redProgramFlag =
          typeof appConfig.config['red_program_flag'] !== 'undefined'
            ? appConfig.config['red_program_flag'].toLowerCase() === 'true'
            : false;
      });
    this.store
      .select(selectActiveSite)
      .pipe(
        take(1),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(activeSite => {
        if (activeSite) {
          this.activeSite = activeSite;
          this.forestDataUpdatedDate = moment(this.activeSite.cohort.region.refreshDate, true).format('MMM D, YYYY');
          this.allowedClearing = this.activeSite.allowedClearing.toFixed(1);
          this.showFirewoodQuestion = this.activeSite.getFirewoodDeductionRange(this.activeSite.cohortId)[0] !== 0;
          this.getReportConfig();
        } else {
          this.router.navigate([`/site/${this.siteId}/map-site`]);
        }
      });
  }

  ngOnInit(): void {
    this.store
      .select(selectCurrentUser)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.currentUser = user;
        this.preferredBuyer = this.currentUser?.preferredBuyer;
      });
  }

  onSubmit(): void {
    if (this.invalidAnsweredQuestions) {
      window.scrollTo(0, 0);
    } else {
      let programs = [];
      let otherPrograms = null;
      otherPrograms = this.selectedPrograms
        .filter(p => !p.id)
        .map(p => p.name)
        .join(',');
      programs = this.selectedPrograms.filter(p => p.id);
      let response = new ReportResponse();
      response.siteId = this.activeSite.id;
      response.reportConfigId = this.reportConfig.id;
      response.reportType = this.reportConfig.reportType;
      response.answers = [];
      this.reportConfig.config.forEach((question: any) => {
        let answer = this.eligibilityForm.value['Custom_question_' + question.sequence];
        let comments = this.eligibilityForm.value['Custom_question_comments_' + question.sequence];
        if (question.special_type == 'program' && answer == question.expected_answer) {
          programs = [];
          otherPrograms = null;
        }
        response.answers.push({
          sequence: question.sequence,
          special_type: question.special_type,
          answer: answer,
          comments: comments,
        });
      });
      this.siteService
        .updateSiteEligibilityReport(this.activeSite.id, programs, otherPrograms, response)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(
          savedSite => {
            this.eligibilityForm.markAsPristine();
            this.store.dispatch(
              updateSubmitEligibilitySiteSuccessAndNavigate({
                data: savedSite,
              })
            );
          },
          error => this.store.dispatch(updateSiteFailure({ error }))
        );
    }
  }

  hasRedProgram(): boolean {
    return this.redProgramFlag && this.selectedPrograms.findIndex(p => p.restrictionLevel === 2) > -1;
  }

  selectProgram(programs: Program[]) {
    this.selectedPrograms = programs;
  }

  get isFormDirty(): boolean {
    return this.eligibilityForm.dirty;
  }

  get invalidAnsweredQuestions(): boolean {
    const controls = this.eligibilityForm.controls;

    // Iterate over all form controls
    for (const name in controls) {
      const control = controls[name];

      // Check if the control is dirty or touched and invalid
      if ((control.dirty || control.touched) && control.invalid) {
        return true; // There is at least one invalid answered question
      }
    }

    // No invalid answered question
    return this.hasRedProgram();
  }

  getReportConfig() {
    this.reportService
      .getReportConfig(this.activeSite.cohort.methodology, ReportType.Eligibility)
      .subscribe(response => {
        this.reportConfig = response;
      });
  }
}
